import React, { useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import LinearProgress from "@material-ui/core/LinearProgress";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import GetAppIcon from "@material-ui/icons/GetApp";
import Alert from "@material-ui/lab/Alert";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

// feat_417 (CK49) — importador de contactos desde CSV en 4 pasos:
// archivo → columnas (vista previa + mapeo) → opciones → resultado.
// El archivo se vuelve a mandar en el paso final (el backend no guarda nada
// entre la vista previa y la importación). No envía mensajes.

const MAX_BYTES = 5 * 1024 * 1024;

const FIELDS = ["ignore", "name", "lastName", "number", "email", "organization", "notes", "tags", "extra"];
const SINGLE = ["name", "lastName", "number", "email", "organization", "notes", "tags"];

const COUNTRIES = ["AR", "UY", "CL", "PY", "BO", "PE", "CO", "EC", "VE", "MX", "BR", "ES", "IT", "US"];

// País por defecto a partir de la zona horaria de la empresa.
const TZ_COUNTRY = {
  "America/Argentina": "AR", "America/Buenos_Aires": "AR", "America/Cordoba": "AR",
  "America/Montevideo": "UY", "America/Santiago": "CL", "America/Asuncion": "PY",
  "America/La_Paz": "BO", "America/Lima": "PE", "America/Bogota": "CO",
  "America/Guayaquil": "EC", "America/Caracas": "VE", "America/Mexico_City": "MX",
  "America/Sao_Paulo": "BR", "Europe/Madrid": "ES", "Europe/Rome": "IT",
  "America/New_York": "US", "America/Chicago": "US", "America/Los_Angeles": "US"
};

export const countryFromTimezone = (tz) => {
  if (!tz) return "AR";
  const hit = Object.keys(TZ_COUNTRY).find((k) => tz === k || tz.startsWith(`${k}/`));
  return hit ? TZ_COUNTRY[hit] : "AR";
};

const countryLabel = (code) => {
  try {
    const dn = new Intl.DisplayNames([i18n.language || "es"], { type: "region" });
    return dn.of(code) || code;
  } catch {
    return code;
  }
};

const useStyles = makeStyles((theme) => ({
  drop: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(4, 2),
    textAlign: "center",
    cursor: "pointer",
    color: theme.palette.text.secondary,
    "&:hover, &$dragging": { borderColor: theme.palette.primary.main, color: theme.palette.text.primary },
  },
  dragging: {},
  stepper: { padding: theme.spacing(1, 0, 2), background: "transparent" },
  tableWrap: {
    overflowX: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    marginTop: theme.spacing(1),
  },
  headCell: { minWidth: 150, verticalAlign: "top", background: theme.palette.background.default },
  cell: { whiteSpace: "nowrap", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" },
  row: { display: "flex", flexWrap: "wrap", gap: theme.spacing(2), alignItems: "center" },
  smallField: { minWidth: 160 },
  summary: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gap: theme.spacing(1),
    margin: theme.spacing(1, 0, 2),
  },
  stat: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    textAlign: "center",
  },
  statNum: { fontSize: 26, fontWeight: 700, lineHeight: 1.2 },
}));

const delimiterToApi = (d) => (d === "\t" ? "tab" : d);

const ContactCsvImportModal = ({ open, onClose, onImported, defaultCountry = "AR" }) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const inputRef = useRef(null);

  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inspection, setInspection] = useState(null);
  const [readOpts, setReadOpts] = useState({ delimiter: "", encoding: "", hasHeader: null });
  const [mapping, setMapping] = useState([]);
  const [country, setCountry] = useState(defaultCountry);
  const [duplicateMode, setDuplicateMode] = useState("skip");
  const [useTag, setUseTag] = useState(false);
  const [tagName, setTagName] = useState("");
  const [result, setResult] = useState(null);

  const reset = () => {
    setStep(0);
    setFile(null);
    setInspection(null);
    setReadOpts({ delimiter: "", encoding: "", hasHeader: null });
    setMapping([]);
    setCountry(defaultCountry);
    setDuplicateMode("skip");
    setUseTag(false);
    setTagName("");
    setResult(null);
    setLoading(false);
  };

  const handleClose = () => {
    if (loading) return;
    const imported = !!result;
    reset();
    onClose();
    if (imported && onImported) onImported();
  };

  const buildForm = (f, opts) => {
    const form = new FormData();
    form.append("file", f);
    if (opts.delimiter) form.append("delimiter", delimiterToApi(opts.delimiter));
    if (opts.encoding) form.append("encoding", opts.encoding);
    if (opts.hasHeader !== null) form.append("hasHeader", String(opts.hasHeader));
    return form;
  };

  const runPreview = async (f, opts) => {
    setLoading(true);
    try {
      const { data } = await api.post("/contacts/import-csv/preview", buildForm(f, opts));
      setInspection(data);
      setMapping(data.suggestedMapping);
      setReadOpts({ delimiter: data.delimiter, encoding: data.encoding, hasHeader: data.hasHeader });
      setStep(1);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const pickFile = (f) => {
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toastError({ response: { data: { error: "ERR_CONTACT_IMPORT_TOO_LARGE" } } });
      return;
    }
    setFile(f);
    runPreview(f, { delimiter: "", encoding: "", hasHeader: null });
  };

  const changeReadOpt = (key, value) => {
    const next = { ...readOpts, [key]: value };
    setReadOpts(next);
    runPreview(file, next);
  };

  const setField = (col, field) => {
    setMapping((prev) =>
      prev.map((f, i) => {
        if (i === col) return field;
        // Un campo simple solo en una columna: la anterior pasa a "ignorar".
        if (SINGLE.includes(field) && f === field) return "ignore";
        return f;
      })
    );
  };

  const hasNumber = mapping.includes("number");
  const numberCol = mapping.indexOf("number");

  const headersLabel = useMemo(() => {
    if (!inspection) return [];
    return inspection.headers.map((h, i) =>
      h || i18n.t("contactImportCsv.columnN", { n: i + 1 })
    );
  }, [inspection]);

  const runImport = async () => {
    setLoading(true);
    try {
      const form = buildForm(file, readOpts);
      form.append("mapping", JSON.stringify(mapping));
      form.append("defaultCountry", country);
      form.append("duplicateMode", duplicateMode);
      form.append("lang", (i18n.language || "es").slice(0, 2));
      if (useTag && tagName.trim()) form.append("tagName", tagName.trim());
      const { data } = await api.post("/contacts/import-csv", form);
      setResult(data);
      setStep(3);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const downloadRejected = () => {
    if (!result?.rejectedCsv) return;
    const blob = new Blob([result.rejectedCsv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = i18n.t("contactImportCsv.result.rejectedFileName");
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const defaultTag = () => {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return i18n.t("contactImportCsv.options.tagDefault", { date: `${dd}/${mm}` });
  };

  const steps = ["file", "columns", "options", "result"].map((k) =>
    i18n.t(`contactImportCsv.steps.${k}`)
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
      <DialogTitle>{i18n.t("contactImportCsv.title")}</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} alternativeLabel className={classes.stepper}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{fullScreen ? "" : label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {loading && <LinearProgress style={{ marginBottom: 12 }} />}

        {step === 0 && (
          <>
            <Typography variant="body2" color="textSecondary" paragraph>
              {i18n.t("contactImportCsv.file.help")}
            </Typography>
            <div
              data-csv-drop
              className={`${classes.drop} ${dragging ? classes.dragging : ""}`}
              onClick={() => inputRef.current && inputRef.current.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                pickFile(e.dataTransfer.files && e.dataTransfer.files[0]);
              }}
            >
              <CloudUploadIcon style={{ fontSize: 44 }} />
              <Typography variant="subtitle1">{i18n.t("contactImportCsv.file.drop")}</Typography>
              <Typography variant="caption">{i18n.t("contactImportCsv.file.limits")}</Typography>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.txt,text/csv,text/plain"
                style={{ display: "none" }}
                onChange={(e) => {
                  pickFile(e.target.files && e.target.files[0]);
                  e.target.value = "";
                }}
              />
            </div>
            <Alert severity="info" style={{ marginTop: 16 }}>
              {i18n.t("contactImportCsv.file.noMessages")}
            </Alert>
          </>
        )}

        {step === 1 && inspection && (
          <>
            <Typography variant="body2" paragraph>
              {i18n.t("contactImportCsv.columns.summary", {
                file: file?.name || "",
                rows: inspection.totalRows,
              })}
            </Typography>
            <div className={classes.row}>
              <TextField
                select
                size="small"
                variant="outlined"
                className={classes.smallField}
                label={i18n.t("contactImportCsv.columns.delimiter")}
                value={readOpts.delimiter}
                onChange={(e) => changeReadOpt("delimiter", e.target.value)}
              >
                {[",", ";", "\t", "|"].map((d) => (
                  <MenuItem key={delimiterToApi(d)} value={d}>
                    {i18n.t(`contactImportCsv.delimiters.${delimiterToApi(d) === "," ? "comma" : delimiterToApi(d) === ";" ? "semicolon" : delimiterToApi(d) === "|" ? "pipe" : "tab"}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                variant="outlined"
                className={classes.smallField}
                label={i18n.t("contactImportCsv.columns.encoding")}
                value={readOpts.encoding}
                onChange={(e) => changeReadOpt("encoding", e.target.value)}
              >
                {["utf-8", "utf-8-bom", "windows-1252", "utf-16le", "utf-16be"].map((enc) => (
                  <MenuItem key={enc} value={enc}>
                    {i18n.t(`contactImportCsv.encodings.${enc.replace(/-/g, "_")}`)}
                  </MenuItem>
                ))}
              </TextField>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={!!readOpts.hasHeader}
                    onChange={(e) => changeReadOpt("hasHeader", e.target.checked)}
                  />
                }
                label={i18n.t("contactImportCsv.columns.hasHeader")}
              />
            </div>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
              {i18n.t("contactImportCsv.columns.mapHelp")}
            </Typography>
            {!hasNumber && (
              <Alert severity="warning" style={{ marginTop: 8 }}>
                {i18n.t("contactImportCsv.columns.numberRequired")}
              </Alert>
            )}
            <div className={classes.tableWrap}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {headersLabel.map((h, col) => (
                      <TableCell key={col} className={classes.headCell}>
                        <Typography variant="caption" display="block" noWrap title={h}>
                          {h}
                        </Typography>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          variant="outlined"
                          value={mapping[col] || "ignore"}
                          onChange={(e) => setField(col, e.target.value)}
                          inputProps={{ "aria-label": h, "data-map-col": col }}
                        >
                          {FIELDS.map((f) => (
                            <MenuItem key={f} value={f}>
                              {i18n.t(`contactImportCsv.fields.${f}`)}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inspection.previewRows.map((r, i) => (
                    <TableRow key={i}>
                      {r.map((c, col) => (
                        <TableCell
                          key={col}
                          className={classes.cell}
                          title={c}
                          style={{
                            opacity: mapping[col] === "ignore" ? 0.45 : 1,
                            fontWeight: col === numberCol ? 600 : 400,
                          }}
                        >
                          {c}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <Typography variant="caption" color="textSecondary">
              {i18n.t("contactImportCsv.columns.previewNote", {
                shown: inspection.previewRows.length,
                total: inspection.totalRows,
              })}
            </Typography>
          </>
        )}

        {step === 2 && (
          <>
            <TextField
              select
              size="small"
              variant="outlined"
              label={i18n.t("contactImportCsv.options.country")}
              helperText={i18n.t("contactImportCsv.options.countryHelp")}
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              style={{ minWidth: 260, marginBottom: 16 }}
            >
              {COUNTRIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {countryLabel(c)}
                </MenuItem>
              ))}
            </TextField>

            <Typography variant="subtitle2">{i18n.t("contactImportCsv.options.duplicates")}</Typography>
            <RadioGroup value={duplicateMode} onChange={(e) => setDuplicateMode(e.target.value)}>
              <FormControlLabel
                value="skip"
                control={<Radio color="primary" />}
                label={i18n.t("contactImportCsv.options.dupSkip")}
              />
              <FormControlLabel
                value="fill_empty"
                control={<Radio color="primary" />}
                label={i18n.t("contactImportCsv.options.dupFill")}
              />
            </RadioGroup>
            <Typography variant="caption" color="textSecondary" display="block" paragraph>
              {i18n.t("contactImportCsv.options.dupHelp")}
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={useTag}
                  onChange={(e) => {
                    setUseTag(e.target.checked);
                    if (e.target.checked && !tagName) setTagName(defaultTag());
                  }}
                />
              }
              label={i18n.t("contactImportCsv.options.tag")}
            />
            {useTag && (
              <TextField
                size="small"
                variant="outlined"
                fullWidth
                inputProps={{ maxLength: 50 }}
                label={i18n.t("contactImportCsv.options.tagName")}
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
                style={{ marginTop: 8 }}
              />
            )}
            <Alert severity="info" style={{ marginTop: 16 }}>
              {i18n.t("contactImportCsv.options.confirm", { rows: inspection?.totalRows || 0 })}
            </Alert>
          </>
        )}

        {step === 3 && result && (
          <>
            <div className={classes.summary}>
              {[
                ["created", result.created, theme.palette.success?.main || "#2e7d32"],
                ["updated", result.updated, theme.palette.primary.main],
                ["skipped", result.skipped, theme.palette.text.secondary],
                ["invalid", result.invalid, theme.palette.error.main],
              ].map(([k, n, color]) => (
                <div key={k} className={classes.stat} data-result={k}>
                  <div className={classes.statNum} style={{ color }}>{n}</div>
                  <Typography variant="caption">{i18n.t(`contactImportCsv.result.${k}`)}</Typography>
                </div>
              ))}
            </div>
            {result.tagsIgnored > 0 && (
              <Alert severity="warning" style={{ marginBottom: 12 }}>
                {i18n.t("contactImportCsv.result.tagsIgnored", { n: result.tagsIgnored })}
              </Alert>
            )}
            {result.rejectedCount > 0 ? (
              <>
                <div className={classes.row} style={{ justifyContent: "space-between" }}>
                  <Typography variant="subtitle2">
                    {i18n.t("contactImportCsv.result.rejectedTitle", { n: result.rejectedCount })}
                  </Typography>
                  <Button size="small" variant="outlined" startIcon={<GetAppIcon />} onClick={downloadRejected}>
                    {i18n.t("contactImportCsv.result.download")}
                  </Button>
                </div>
                <div className={classes.tableWrap}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{i18n.t("contactImportCsv.result.line")}</TableCell>
                        <TableCell>{i18n.t("contactImportCsv.fields.number")}</TableCell>
                        <TableCell>{i18n.t("contactImportCsv.result.reason")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.rejectedSample.map((r) => (
                        <TableRow key={r.line}>
                          <TableCell>{r.line}</TableCell>
                          <TableCell className={classes.cell}>{r.number || "—"}</TableCell>
                          <TableCell>{r.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {result.rejectedCount > result.rejectedSample.length && (
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("contactImportCsv.result.moreInFile")}
                  </Typography>
                )}
              </>
            ) : (
              <Alert severity="success">{i18n.t("contactImportCsv.result.allOk")}</Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {step < 3 && (
          <Button onClick={handleClose} disabled={loading}>
            {i18n.t("contactImportCsv.buttons.cancel")}
          </Button>
        )}
        {(step === 1 || step === 2) && (
          <Button onClick={() => setStep(step === 1 ? 0 : 1)} disabled={loading}>
            {i18n.t("contactImportCsv.buttons.back")}
          </Button>
        )}
        {step === 1 && (
          <Button color="primary" variant="contained" disabled={!hasNumber || loading} onClick={() => setStep(2)}>
            {i18n.t("contactImportCsv.buttons.next")}
          </Button>
        )}
        {step === 2 && (
          <Button color="primary" variant="contained" disabled={loading || (useTag && !tagName.trim())} onClick={runImport}>
            {i18n.t("contactImportCsv.buttons.import")}
          </Button>
        )}
        {step === 3 && (
          <Button color="primary" variant="contained" onClick={handleClose}>
            {i18n.t("contactImportCsv.buttons.close")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ContactCsvImportModal;
